import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../../hooks/useToast';
import { ProjectPicker } from '../../components/ui/ProjectPicker';
import {
  changeManagementService,
  ChangeItem,
  ChangeImpact,
} from '../../services/change-management.service';
import { projectService, Project } from '../../services/project.service';

const UNITS = ['m²', 'm³', 'ml', 'pza', 'kg', 'ton', 'hr', 'día'];

const CATEGORIES = [
  { value: 'structural', label: 'Estructural' },
  { value: 'finishes', label: 'Acabados' },
  { value: 'systems', label: 'Sistemas' },
  { value: 'layout', label: 'Distribución' },
  { value: 'other', label: 'Otro' },
];

const PRIORITIES = [
  { value: 'low', label: 'Baja' },
  { value: 'medium', label: 'Media' },
  { value: 'high', label: 'Alta' },
  { value: 'critical', label: 'Crítica' },
];

export function ChangeOrderFormPage() {
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const [searchParams] = useSearchParams();
  const initialProjectId = searchParams.get('projectId') ?? '';

  const [projectId, setProjectId] = useState(initialProjectId);
  const [project, setProject] = useState<Project | null>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('other');
  const [priority, setPriority] = useState('medium');

  const [items, setItems] = useState<ChangeItem[]>([
    {
      description: '',
      quantity: 1,
      unit: 'm²',
      unitPrice: 0,
      total: 0,
      type: 'addition',
      category: 'general',
    },
  ]);

  const [isSaving, setIsSaving] = useState(false);

  const impact: ChangeImpact = useMemo(
    () => changeManagementService.calculateImpact(items, project?.budget ?? 0),
    [items, project?.budget]
  );

  const handleProjectChange = async (id: string) => {
    setProjectId(id);
    if (!id) {
      setProject(null);
      return;
    }
    try {
      const p = await projectService.getProject(id);
      setProject(p);
    } catch (e) {
      setProject(null);
    }
  };

  const updateItem = (index: number, patch: Partial<ChangeItem>) => {
    const next = [...items];
    next[index] = { ...next[index], ...patch };
    if (patch.quantity !== undefined || patch.unitPrice !== undefined) {
      next[index].total = (next[index].quantity || 0) * (next[index].unitPrice || 0);
    }
    setItems(next);
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        description: '',
        quantity: 1,
        unit: 'm²',
        unitPrice: 0,
        total: 0,
        type: 'addition',
        category: 'general',
      },
    ]);
  };

  const removeItem = (index: number) => {
    if (items.length <= 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId) {
      showError('Selecciona un proyecto');
      return;
    }
    if (!title.trim() || !description.trim()) {
      showError('Completa el título y la descripción');
      return;
    }
    if (items.some((i) => !i.description.trim() || i.unitPrice <= 0)) {
      showError('Completa descripción y precio de todas las partidas');
      return;
    }

    setIsSaving(true);
    try {
      await changeManagementService.createChangeOrder({
        projectId,
        title: title.trim(),
        description: description.trim(),
        category,
        priority,
        items,
        impact,
      });
      showSuccess('Orden de cambio creada');
      navigate('/gestion/ordenes-cambio', { replace: true });
    } catch (err: any) {
      showError(err.message || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const formatCurrency = (n: number) =>
    new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      maximumFractionDigits: 0,
    }).format(n || 0);

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-slate-500 flex items-center gap-1"
      >
        ← Volver
      </button>

      <div>
        <h1 className="text-xl font-bold text-slate-900">Nueva Orden de Cambio</h1>
        <p className="text-xs text-slate-500">El impacto se recalcula automáticamente</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Proyecto *
            </label>
            <ProjectPicker
              value={projectId}
              onChange={(id) => handleProjectChange(id)}
              required
              filterStatus={['planning', 'in_progress', 'on_hold']}
            />
            {project && (
              <p className="text-[11px] text-slate-500 mt-1">
                Presupuesto actual: {formatCurrency(project.budget)}
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Título *
            </label>
            <input
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ej. Cambio de acabados en cocina"
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Descripción *
            </label>
            <textarea
              required
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm resize-none focus:border-primary focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Categoría
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Prioridad
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:border-primary focus:outline-none"
              >
                {PRIORITIES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-900">Partidas</h2>
            <button
              type="button"
              onClick={addItem}
              className="text-xs text-primary font-semibold"
            >
              + Agregar
            </button>
          </div>

          {items.map((item, idx) => (
            <div key={idx} className="bg-slate-50 rounded-lg p-3 space-y-2">
              <div className="flex gap-2">
                <select
                  value={item.type}
                  onChange={(e) =>
                    updateItem(idx, {
                      type: e.target.value as 'addition' | 'deduction' | 'modification',
                    })
                  }
                  className="flex-1 px-2 py-1.5 rounded border border-slate-200 text-xs"
                >
                  <option value="addition">Adición</option>
                  <option value="deduction">Deducción</option>
                  <option value="modification">Modificación</option>
                </select>
                {items.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeItem(idx)}
                    className="px-2 text-red-600 text-sm"
                  >
                    ✕
                  </button>
                )}
              </div>
              <input
                type="text"
                placeholder="Descripción"
                value={item.description}
                onChange={(e) => updateItem(idx, { description: e.target.value })}
                className="w-full px-2 py-1.5 rounded border border-slate-200 text-xs"
              />
              <div className="grid grid-cols-3 gap-2">
                <input
                  type="number"
                  placeholder="Cant."
                  min="0"
                  step="0.01"
                  value={item.quantity}
                  onChange={(e) =>
                    updateItem(idx, { quantity: parseFloat(e.target.value) || 0 })
                  }
                  className="px-2 py-1.5 rounded border border-slate-200 text-xs"
                />
                <select
                  value={item.unit}
                  onChange={(e) => updateItem(idx, { unit: e.target.value })}
                  className="px-2 py-1.5 rounded border border-slate-200 text-xs"
                >
                  {UNITS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  placeholder="P. Unit."
                  min="0"
                  step="0.01"
                  value={item.unitPrice}
                  onChange={(e) =>
                    updateItem(idx, { unitPrice: parseFloat(e.target.value) || 0 })
                  }
                  className="px-2 py-1.5 rounded border border-slate-200 text-xs"
                />
              </div>
              <p className="text-right text-xs text-slate-700 font-semibold">
                {formatCurrency((item.quantity || 0) * (item.unitPrice || 0))}
              </p>
            </div>
          ))}
        </div>

        <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
          <p className="text-xs font-semibold text-primary mb-2">Impacto calculado</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-600">Presupuesto actual</span>
              <span className="font-medium">{formatCurrency(impact.originalBudget)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Adiciones</span>
              <span className="text-red-600">+{formatCurrency(impact.itemsSummary.additions)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Deducciones</span>
              <span className="text-emerald-600">
                -{formatCurrency(impact.itemsSummary.deductions)}
              </span>
            </div>
            <div className="flex justify-between pt-1 border-t border-primary/20">
              <span className="font-semibold">Nuevo presupuesto</span>
              <span className="font-bold text-primary">{formatCurrency(impact.newBudget)}</span>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex-1 py-2.5 rounded-lg bg-slate-100 text-slate-700 font-semibold text-sm"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="flex-1 py-2.5 rounded-lg bg-primary text-white font-semibold text-sm disabled:opacity-50"
          >
            {isSaving ? 'Guardando…' : 'Crear'}
          </button>
        </div>
      </form>
    </div>
  );
}